/**
 * Shared validation patterns (WRK-5900).
 *
 * The single source of truth for the "safe free text" rule already enforced
 * client-side in apps/works-lite/src/shared/lib/validation.ts. That copy is
 * frontend-only code (not importable from the backend), so this is a second,
 * deliberately identical pattern for server-side Zod schemas — keep both in
 * sync if the rule ever changes.
 */

/** Letters, numbers, spaces, commas, full stops, hyphens, apostrophes, forward slashes. */
export const SAFE_TEXT_PATTERN = /^[A-Za-z0-9\s,.'/-]+$/;

export const SAFE_TEXT_VALIDATION_MSG =
  'Only letters, numbers, spaces, commas, full stops, hyphens, apostrophes, and forward slashes are allowed';

/**
 * UK postcode (SC-6525). All six outward-code shapes (A9, A99, A9A, AA9, AA99,
 * AA9A) plus the GIR 0AA special case, then the inward code (9AA). The space is
 * optional here so a client that sends the compact form still passes; the Lite
 * UI normalises to `LS1 4AP` before submitting.
 */
export const POSTCODE_PATTERN = /^([A-Z]{1,2}\d[A-Z\d]?|GIR)\s?\d[A-Z]{2}$/i;

export const POSTCODE_VALIDATION_MSG = 'Enter a valid UK postcode (e.g. LS1 4AP)';

/**
 * Invoice raised with a validation request or completion (CWL-164). Letters,
 * digits and hyphens only, max 15 characters; amount is money — at most 2
 * decimal places and no more than 999,999.99 (6 integer digits). Over-long or
 * punctuated references broke CORE's Validation tab. These are CORE's limits: no
 * local column constrains them, so they cannot be derived from the schema.
 */
export const INVOICE_REFERENCE_PATTERN = /^[A-Za-z0-9-]+$/;

export const INVOICE_REFERENCE_MAX_LENGTH = 15;

/**
 * Deliberately short and generic: the two invoice fields sit side by side in a
 * narrow dialog, where a message spelling out the rule wrapped to three lines.
 */
export const INVOICE_REFERENCE_VALIDATION_MSG = 'Invalid reference';

export const INVOICE_AMOUNT_MAX = 999_999.99;

export const INVOICE_AMOUNT_VALIDATION_MSG = 'Invalid amount';
